import { Suspense, lazy, type ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";

/*
  The charts, loaded when one is first drawn (#565).

  Recharts is about 120 kB gzipped, a quarter again on top of the whole app,
  and most of what anyone opens here is a list. Importing a chart from this
  file rather than from `charts.tsx` keeps the library out of the bundle every
  page pays for: it arrives with the first dashboard or report, once, and a
  grey box the chart's own size holds its place for the moment that takes.
*/

type Charts = typeof import("@/components/charts/charts");

function deferred<Props extends object>(
  pick: (charts: Charts) => (props: Props) => ReactNode,
  /** The box the chart will fill, so nothing below it moves when it lands. */
  placeholder: string,
) {
  const Chart = lazy(async () => ({
    default: pick(await import("@/components/charts/charts")),
  }));
  return function DeferredChart(props: Props) {
    return (
      <Suspense fallback={<Skeleton className={placeholder} />}>
        <Chart {...props} />
      </Suspense>
    );
  };
}

export const TimeSeriesChart = deferred(
  (charts) => charts.TimeSeriesChart,
  "h-64 w-full",
);
export const MultiLineChart = deferred(
  (charts) => charts.MultiLineChart,
  "h-64 w-full",
);
export const RankedBarChart = deferred(
  (charts) => charts.RankedBarChart,
  "h-40 w-full",
);
export const CategoryBarChart = deferred(
  (charts) => charts.CategoryBarChart,
  "h-64 w-full",
);
export const DonutChart = deferred(
  (charts) => charts.DonutChart,
  "h-48 w-full",
);
export const Sparkline = deferred(
  (charts) => charts.Sparkline,
  "mt-3 h-10 w-full",
);

export type { LineSeries } from "@/components/charts/charts";
